import { CartErrorEvent, CartLinesUpdateEvent } from '@shopify/events';

/** Connect the custom homepage cards to Horizon's native cart drawer. */
document.addEventListener('submit', async (event) => {
  const form = event.target instanceof HTMLFormElement ? event.target : null;
  if (!form?.matches('.pg-custom-card__form')) return;

  event.preventDefault();

  const button = form.querySelector('button[type="submit"]');
  if (button?.disabled) return;

  const formData = new FormData(form);
  const quantity = Number(formData.get('quantity')) || 1;
  const variantId = String(formData.get('id') || '');
  const sectionIds = Array.from(document.querySelectorAll('cart-items-component[data-section-id]'))
    .map((component) => component.dataset.sectionId)
    .filter(Boolean);

  if (sectionIds.length) formData.set('sections', sectionIds.join(','));

  const deferredEvent = CartLinesUpdateEvent.createPromise();
  form.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: [{ merchandiseId: variantId, quantity }],
      promise: deferredEvent.promise,
    })
  );

  const originalMarkup = button?.innerHTML || 'Add to cart';
  let restoreTimer;
  const restoreButton = () => {
    if (!button) return;
    button.disabled = false;
    button.removeAttribute('aria-busy');
    button.innerHTML = originalMarkup;
  };

  if (button) {
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    button.textContent = 'Adding...';
    // Match Horizon's short feedback cycle and never leave the control stuck.
    restoreTimer = window.setTimeout(restoreButton, 1800);
  }

  try {
    const response = await fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body: formData,
    });
    const result = await response.json();

    if (!response.ok || result.status) {
      throw new Error(result.description || result.message || 'Unable to add this item');
    }

    if (button) {
      button.textContent = 'Added';
      window.clearTimeout(restoreTimer);
      restoreTimer = window.setTimeout(restoreButton, 650);
    }

    // The item is already added at this point. Refresh the drawer separately so
    // slow cart rendering never holds the product button or another card.
    fetch(`${Theme.routes.cart_url}.json`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    })
      .then((cartResponse) => {
        if (!cartResponse.ok) throw new Error('Unable to refresh the cart');
        return cartResponse.json();
      })
      .then((cart) => {
        deferredEvent.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: {
            items: cart.items,
            itemCount: quantity,
            sections: result.sections,
            source: 'homepage-product-card',
            sourceId: form.id || variantId,
            didError: false,
          },
        });
      })
      .catch((error) => deferredEvent.reject(error));
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Unable to add this item';
    deferredEvent.reject(error);
    form.dispatchEvent(new CartErrorEvent({ error: message, code: 'ADD_TO_CART_ERROR' }));

    if (button) {
      button.textContent = 'Try again';
      window.clearTimeout(restoreTimer);
      restoreTimer = window.setTimeout(restoreButton, 1400);
    }
  }
});
